import { useEffect, useRef, useState } from "react";
import { backToLobby, matchEnded, type Session, wsUrlFor } from "../app/store.ts";
import { Arena } from "../arena/arena.ts";
import { crushPop, damagePops, type PopHit } from "../arena/damage-numbers.ts";
import { hitSeverity } from "../arena/hit-flash.ts";
import { loadSoundSettings, Sfx, ThrowSounds } from "../audio/sfx.ts";
import { Icon } from "../components/ui/index.tsx";
import { ControlsGrid } from "../hud/ControlsGrid.tsx";
import { Hud } from "../hud/Hud.tsx";
import { fmt, t } from "../i18n/index.ts";
import { Controls, loadLookSettings } from "../input/controls.ts";
import {
  type ClientView,
  GameClient,
  type GameEvent,
  RENDER_DELAY_MS,
  type WaitingView,
} from "../net/game-client.ts";
import { formatPercent } from "../utils/format.ts";
import { WaitingRoom } from "./WaitingRoom.tsx";

const HUD_HZ = 10;

function hitLine(e: Record<string, unknown>, nameOf: (id: unknown) => string): string {
  const num = (k: string) => (typeof e[k] === "number" ? (e[k] as number) : 0);
  const tags: string[] = [];
  if (e.parried) tags.push(t.match.hit.parried);
  else if (e.checked) tags.push(fmt(t.match.hit.checked, { recoil: num("recoil").toFixed(1) }));
  else if (e.blocked)
    tags.push(fmt(t.match.hit.blocked, { absorb: Math.round(num("absorb") * 100) }));
  if (e.collapsed) tags.push(t.match.hit.collapsed);
  if (e.counter) tags.push(t.match.hit.counter);
  if (num("bodyFactor") > 1) tags.push(t.match.hit.steppedIn);
  if (e.extension === "jammed") tags.push(t.match.hit.jammed);
  if (e.extension === "overreach") tags.push(t.match.hit.overreach);
  // A label derived from what the strike was, never a move looked up: limb kind, arc, lead or rear.
  const kind = num("limb") < 2 ? t.match.strikes.arm : t.match.strikes.leg;
  const strike = kind[num("arc")]?.[num("lead") > 0 ? 0 : 1] ?? "";
  const line = fmt(t.match.hit.line, {
    attacker: nameOf(e.attacker),
    target: nameOf(e.target),
    strike,
    part: t.match.parts[num("part")] ?? "",
    damage: num("damage").toFixed(1),
  });
  return tags.length ? `${line} (${tags.join(", ")})` : line;
}

/** Reads the fields a flash, a sound and a damage number need from a hit event off the wire. */
function parseHit(e: Record<string, unknown>): PopHit | null {
  const at = e.at as { x?: unknown; y?: unknown; z?: unknown } | undefined;
  if (
    typeof e.attacker !== "number" ||
    typeof e.target !== "number" ||
    typeof e.part !== "number" ||
    typeof e.damage !== "number" ||
    typeof at?.x !== "number" ||
    typeof at.y !== "number" ||
    typeof at.z !== "number"
  )
    return null;
  return {
    attacker: e.attacker,
    target: e.target,
    part: e.part,
    at: { x: at.x, y: at.y, z: at.z },
    damage: e.damage,
    blocked: e.blocked === true,
    parried: e.parried === true,
    checked: e.checked === true,
    collapsed: e.collapsed === true,
    counter: e.counter === true,
    extension: typeof e.extension === "string" ? e.extension : "clean",
    recoil: typeof e.recoil === "number" ? e.recoil : 0,
  };
}

export function Match({ session, nickname }: { session: Session; nickname: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const damageLayerRef = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<ClientView | null>(null);
  const [waiting, setWaiting] = useState<WaitingView | null>(null);
  const [events, setEvents] = useState<GameEvent[]>([]);
  const [locked, setLocked] = useState(false);
  const [hasLocked, setHasLocked] = useState(false);
  const [invertY, setInvertY] = useState(() => loadLookSettings().invertY);
  const [volume, setVolume] = useState(() => loadSoundSettings().volume);
  const controlsRef = useRef<Controls | null>(null);
  const clientRef = useRef<GameClient | null>(null);
  const sfxRef = useRef<Sfx | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const damageLayer = damageLayerRef.current;
    if (!canvas || !damageLayer) return;
    const controls = new Controls(canvas, (isLocked) => {
      setLocked(isLocked);
      if (isLocked) setHasLocked(true);
    });
    controlsRef.current = controls;
    const client = new GameClient({
      url: wsUrlFor(session.wsPath),
      token: session.token,
      nickname,
      controls,
    });
    clientRef.current = client;
    const arena = new Arena(canvas, controls, damageLayer);
    const sfx = new Sfx();
    sfxRef.current = sfx;
    const throws = new ThrowSounds();
    let ended = false;
    let mismatch = false;
    const names = () => client.view(performance.now())?.fighters ?? [];
    const nameOf = (id: unknown) => names().find((f) => f.id === id)?.nickname ?? "?";
    const push = (text: string) =>
      setEvents((prev) => [...prev.slice(-9), { at: performance.now() + Math.random(), text }]);

    const offEvents = client.onEvent((msg) => {
      if (msg.t === "events") {
        for (const e of msg.events) {
          if (e.type === "shrink") push(t.match.events.shrink);
          else if (e.type === "crush") {
            push(fmt(t.match.events.crushed, { name: nameOf(e.id) }));
            const pop =
              typeof e.id === "number" && typeof e.damage === "number"
                ? crushPop(e.id, e.damage, client.myId)
                : null;
            if (pop) arena.popDamage(pop, 0);
          } else if (e.type === "eliminated") {
            const by = typeof e.by === "number" && e.by >= 0 ? nameOf(e.by) : null;
            push(
              by
                ? fmt(t.match.events.eliminatedBy, { name: nameOf(e.id), by })
                : fmt(t.match.events.eliminated, { name: nameOf(e.id) }),
            );
          } else if (e.type === "matchEnd") push(t.match.events.matchEnd);
          else if (e.type === "hit") {
            push(hitLine(e, nameOf));
            const hit = parseHit(e);
            if (!hit) continue;
            // My own strikes confirm at once; everyone else's wait for the interpolated view.
            const mine = hit.attacker === client.myId;
            const delayMs = mine ? 0 : RENDER_DELAY_MS;
            arena.flashHit(hit, delayMs);
            for (const pop of damagePops(hit, client.myId)) arena.popDamage(pop, delayMs);
            const where = arena.worldPoint(hit.target, hit.at);
            if (where) {
              sfx.impact(where, {
                severity: hitSeverity(hit.damage, hit.part),
                blocked: hit.blocked,
                parried: hit.parried,
                mine: mine || hit.target === client.myId,
                delay: delayMs / 1000,
              });
            }
          }
        }
      } else if (msg.t === "error" && client.view(performance.now())) push(msg.code);
    });

    client.connect();
    arena.start((now) => {
      const v = client.view(now);
      if (v) {
        const me = v.fighters[v.myId];
        if (me) sfx.setListener(me.pos.x, me.pos.z, controls.yaw);
        throws.update(v, sfx, RENDER_DELAY_MS / 1000);
      }
      return v;
    });
    const hudTimer = setInterval(() => {
      const v = client.view(performance.now());
      setView(v);
      setWaiting(v ? null : client.waitingView());
      if (v && v.phase === "ended" && !ended) {
        ended = true;
        setTimeout(() => matchEnded(session, nickname, client), 2500);
      }
      if (v && !v.connected && !ended && v.tick > 0) push(t.match.connectionLost);
      if (v?.error === "snapshot_decode" && !mismatch) {
        mismatch = true;
        push(t.match.buildMismatch);
      }
    }, 1000 / HUD_HZ);

    return () => {
      clearInterval(hudTimer);
      offEvents();
      arena.dispose();
      controls.dispose();
      sfx.dispose();
      clientRef.current = null;
      sfxRef.current = null;
      if (!ended) client.close();
    };
  }, [session, nickname]);

  return (
    <div className="match">
      <canvas ref={canvasRef} className="match-canvas" />
      <div ref={damageLayerRef} className="damage-layer" aria-hidden="true" />
      {view ? <Hud view={view} events={events} locked={locked} /> : null}
      {!view ? (
        <div className="overlay overlay-paper">
          <WaitingRoom
            waiting={waiting}
            session={session}
            onStart={() => clientRef.current?.requestStart()}
            onLeave={() => backToLobby()}
          />
        </div>
      ) : !locked && view.phase !== "ended" ? (
        <PausePanel
          started={hasLocked}
          invertY={invertY}
          volume={volume}
          onInvertY={(next) => {
            controlsRef.current?.setInvertY(next);
            setInvertY(next);
          }}
          onVolume={(next) => {
            sfxRef.current?.setVolume(next);
            setVolume(next);
          }}
          onResume={() => controlsRef.current?.requestLock()}
          onLeave={() => backToLobby()}
        />
      ) : null}
    </div>
  );
}

/** Esc releases pointer lock, so this doubles as the pause menu and the settings. */
function PausePanel({
  started,
  invertY,
  volume,
  onInvertY,
  onVolume,
  onResume,
  onLeave,
}: {
  started: boolean;
  invertY: boolean;
  volume: number;
  onInvertY: (next: boolean) => void;
  onVolume: (next: number) => void;
  onResume: () => void;
  onLeave: () => void;
}) {
  return (
    <div className="overlay">
      <div className="card pause-card stack stack-4">
        <div className="pause-title">
          <h2>{started ? t.match.paused : t.match.clickToPlay}</h2>
          <p className="muted">{t.match.pausedHint}</p>
        </div>

        <div className="pause-section">{t.match.controlsTitle}</div>
        <ControlsGrid />

        <div className="pause-section">{t.match.settingsTitle}</div>
        <label className="check">
          <span className="check-text">
            {t.match.settings.invertY}
            <span className="check-hint">{t.match.settings.invertYHint}</span>
          </span>
          <input
            type="checkbox"
            checked={invertY}
            onChange={(e) => onInvertY(e.currentTarget.checked)}
          />
        </label>
        <label className="slider">
          <span className="check-text">
            {t.match.settings.volume}
            <span className="check-hint">{t.match.settings.volumeHint}</span>
          </span>
          <span className="slider-control">
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={Math.round(volume * 100)}
              onChange={(e) => onVolume(Number(e.currentTarget.value) / 100)}
            />
            <output className="num">{formatPercent(volume)}</output>
          </span>
        </label>

        <div className="pause-actions">
          <button type="button" className="btn" onClick={onLeave}>
            <Icon name="exit" />
            {t.match.leave}
          </button>
          <button type="button" className="btn btn-primary btn-lg" onClick={onResume}>
            {started ? t.match.resume : t.match.clickToPlay}
          </button>
        </div>
      </div>
    </div>
  );
}
